import { diff_match_patch } from 'diff-match-patch';
import React, { useEffect, useRef } from 'react';

import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons/faCircleQuestion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from 'antd';
import Button from '../Button/Button';
import Modal from '../Modal/Modal';

type Props = {
  onClose: () => void;
  selectedText: string;
  improvedText: string;
  onAppendBelow: () => void;
  onReplace: () => void;
  onRetry: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  requestCount?: number;
  maxRequestCount?: number;
  error?: string;
};

function AIImprovementPanel({ 
  onClose, 
  selectedText, 
  improvedText, 
  onAppendBelow, 
  onReplace, 
  onRetry,
  onCancel,
  isLoading = false,
  requestCount = 0,
  maxRequestCount = 0,
  error = ''
}: Props) {

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // 포커스를 모달 컨테이너로 주어 키보드 이벤트를 캡처
    containerRef.current?.focus();
  }, []);

  const blockActivationKeys = (e: React.KeyboardEvent) => {
    const isSpace = e.key === ' ' || (e as any).code === 'Space' || (e as any).keyCode === 32;
    const isEnter = e.key === 'Enter' || (e as any).code === 'Enter' || (e as any).keyCode === 13;
    if (isSpace || isEnter) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // diff를 렌더링하는 함수
  const renderDiff = (original: string, improved: string) => {
    const dmp = new diff_match_patch();
    const diffs = dmp.diff_main(original, improved);
    dmp.diff_cleanupSemantic(diffs);
    
    return (
      <div className="diff-view">
        {diffs.map((diff, index) => {
          const [operation, text] = diff;
          let className = 'diff-unchanged';
          
          if (operation === 1) { // DIFF_INSERT
            className = 'diff-added';
          } else if (operation === -1) { // DIFF_DELETE
            className = 'diff-removed';
          }
          
          return (
            <span 
              key={index} 
              className={`diff-part ${className}`}
            >
              {text}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <Modal show={true} close={onClose}>
      <div
        className="ai-improvement-modal"
        ref={containerRef}
        tabIndex={-1}
        onKeyDownCapture={blockActivationKeys}
        onKeyUpCapture={blockActivationKeys}
      >
        <div className="ai-modal-header">
          <div className="title-section">
            <h3>AI로 다듬기</h3>
            {!isLoading && (
              <div className="counter-help">
                <div>({requestCount}/{maxRequestCount})</div>
                <Tooltip 
                  title="워크스페이스 기준 하루에 10번 사용할 수 있습니다." 
                  placement='topRight'
                  align={{offset: [14, -10]}}
                  overlayInnerStyle={{
                    width: '270px',
                    whiteSpace: 'normal',
                    fontSize: '12px'
                  }}
                  zIndex={10004}
                >
                  <FontAwesomeIcon icon={faCircleQuestion} color='var(--gray-600)'  />
                </Tooltip>
              </div>
            )}
          </div>
        </div>
        <div className="ai-modal-content">
          {error ? (
            <>
            <div className="error-section">
              <div className="error-message" dangerouslySetInnerHTML={{ __html: error }}></div>
            </div>
            <div className="close-button">
              <Button onClick={onCancel} size="small" color="gray">
                닫기
              </Button>
            </div>
            </>
          ) : isLoading ? (
            <div className="loading-section">
              <div className="loading-message">
                텍스트를 다듬고 있습니다...
                <br />
                잠시만 기다려주세요.
              </div>
            </div>
          ) : (
            <>
              <div className="comparison-section">
              {/* <div className="original-text">
                  <div className="text-preview">{selectedText}</div>
                </div>
                <div className="improved-text">
                  <div className="text-preview">{improvedText}</div> */}
                <div className="diff-container">
                  <div className="text-preview">
                    {renderDiff(selectedText, improvedText)}
                  </div>
                </div>
              </div>
              <div className="action-buttons">
                <Button onClick={onReplace} size="small" color="gray">
                  바로 적용하기
                </Button>
                <Button onClick={onAppendBelow} size="small" color="gray">
                  뒤에 추가하기
                </Button>
                <Button onClick={onRetry} size="small" color="tertiary">
                  다시 다듬기
                </Button>
                <Button onClick={onCancel} size="small" color="tertiary">
                  취소
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

AIImprovementPanel.defaultProps = {};

export default AIImprovementPanel;